import React from 'react';
import { StatsTabProps } from './types';

const StatsTab: React.FC<StatsTabProps> = ({
  tasks,
  categories,
  statsFilter,
  setStatsFilter,
  statsDateFrom,
  setStatsDateFrom,
  statsDateTo,
  setStatsDateTo
}) => {
  // Фильтрация выполненных задач (включая архивные)
  const filteredTasks = tasks.filter(task => {
    if (task.status !== 'done' && task.status !== 'archived') return false;
    // Используем completedAt если есть, иначе fallback на updatedAt
    const dateToUse = task.completedAt || task.updatedAt;
    if (!dateToUse) return false;
    
    if (statsFilter === 'all') return true;
    
    const taskDate = new Date(dateToUse);
    const taskDateOnly = new Date(taskDate.getFullYear(), taskDate.getMonth(), taskDate.getDate());
    const dateFrom = statsDateFrom ? new Date(statsDateFrom + 'T00:00:00') : null;
    const dateTo = statsDateTo ? new Date(statsDateTo + 'T23:59:59') : null;
    
    if (dateFrom && taskDateOnly < dateFrom) return false;
    if (dateTo && taskDateOnly > dateTo) return false;
    
    return true;
  });

  // Определяем формат и создаём ВСЕ периоды в диапазоне
  let allPeriods: string[] = [];
  let dateFormat: 'hour' | 'day' | 'month' | 'year' = 'day';
  
  if (statsFilter === 'day') {
    dateFormat = 'hour';
    const today = statsDateFrom || new Date().toISOString().split('T')[0];
    for (let h = 0; h < 24; h++) {
      allPeriods.push(`${today}-${String(h).padStart(2, '0')}`);
    }
  } else if (statsFilter === 'month') {
    dateFormat = 'day';
    const dateFrom = new Date(statsDateFrom + 'T00:00:00');
    const dateTo = new Date(statsDateTo + 'T00:00:00');
    
    let currentDate = new Date(dateFrom);
    while (currentDate <= dateTo) {
      const year = currentDate.getFullYear();
      const month = String(currentDate.getMonth() + 1).padStart(2, '0');
      const day = String(currentDate.getDate()).padStart(2, '0');
      allPeriods.push(`${year}-${month}-${day}`);
      currentDate.setDate(currentDate.getDate() + 1);
    }
  } else if (statsFilter === 'year') {
    dateFormat = 'month';
    const year = new Date(statsDateFrom).getFullYear();
    for (let m = 1; m <= 12; m++) {
      allPeriods.push(`${year}-${String(m).padStart(2, '0')}`);
    }
  } else {
    dateFormat = 'year';
    if (filteredTasks.length > 0) {
      const dates = filteredTasks.map(t => new Date(t.completedAt || t.updatedAt!));
      const minYear = Math.min(...dates.map(d => d.getFullYear()));
      const maxYear = Math.max(...dates.map(d => d.getFullYear()));
      
      for (let y = minYear; y <= maxYear; y++) {
        allPeriods.push(String(y));
      }
    } else {
      allPeriods.push(String(new Date().getFullYear()));
    }
  }
  
  // Группировка задач по периодам
  const tasksByPeriod: { [key: string]: number } = {};
  
  allPeriods.forEach(period => {
    tasksByPeriod[period] = 0;
  });
  
  filteredTasks.forEach(task => {
    const dateToUse = task.completedAt || task.updatedAt!;
    const date = new Date(dateToUse);
    let periodKey: string;
    
    if (dateFormat === 'hour') {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hour = String(date.getHours()).padStart(2, '0');
      periodKey = `${year}-${month}-${day}-${hour}`;
    } else if (dateFormat === 'day') {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      periodKey = `${year}-${month}-${day}`;
    } else if (dateFormat === 'month') {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      periodKey = `${year}-${month}`;
    } else {
      periodKey = String(date.getFullYear());
    }
    
    if (tasksByPeriod.hasOwnProperty(periodKey)) {
      tasksByPeriod[periodKey]++;
    }
  });

  const maxCount = Math.max(...Object.values(tasksByPeriod), 1);

  // Статистика по категориям
  const categoryStats: { [key: string]: { count: number; name: string; color: string; icon: string } } = {};
  let uncategorizedCount = 0;
  
  filteredTasks.forEach(task => {
    if (task.category) {
      const catId = task.category.id.toString();
      if (!categoryStats[catId]) {
        categoryStats[catId] = {
          count: 0,
          name: task.category.name,
          color: task.category.color,
          icon: task.category.icon
        };
      }
      categoryStats[catId].count++;
    } else {
      uncategorizedCount++;
    }
  });

  if (uncategorizedCount > 0) {
    categoryStats['uncategorized'] = {
      count: uncategorizedCount,
      name: 'Без категории',
      color: '#9ca3af',
      icon: '📝'
    };
  }

  const totalTasks = filteredTasks.length;
  const categoriesArray = Object.values(categoryStats).sort((a, b) => b.count - a.count);

  return (
    <div className="stats-page">
      {/* Фильтры */}
      <div style={{
        background: '#f9fafb',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '20px'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '15px' }}>Фильтры</h3>
        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
          <button
            onClick={() => setStatsFilter('all')}
            style={{
              padding: '8px 16px',
              background: statsFilter === 'all' ? '#3b82f6' : 'white',
              color: statsFilter === 'all' ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: statsFilter === 'all' ? 'bold' : 'normal'
            }}
          >
            Всё время
          </button>
          <button
            onClick={() => {
              setStatsFilter('day');
              const today = new Date();
              const year = today.getFullYear();
              const month = String(today.getMonth() + 1).padStart(2, '0');
              const day = String(today.getDate()).padStart(2, '0');
              const todayStr = `${year}-${month}-${day}`;
              setStatsDateFrom(todayStr);
              setStatsDateTo(todayStr);
            }}
            style={{
              padding: '8px 16px',
              background: statsFilter === 'day' ? '#3b82f6' : 'white',
              color: statsFilter === 'day' ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: statsFilter === 'day' ? 'bold' : 'normal'
            }}
          >
            Сегодня
          </button>
          <button
            onClick={() => {
              setStatsFilter('month');
              const today = new Date();
              const year = today.getFullYear();
              const month = String(today.getMonth() + 1).padStart(2, '0');
              
              const firstDay = `${year}-${month}-01`;
              const lastDay = new Date(year, today.getMonth() + 1, 0).getDate();
              const lastDayStr = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
              
              setStatsDateFrom(firstDay);
              setStatsDateTo(lastDayStr);
            }}
            style={{
              padding: '8px 16px',
              background: statsFilter === 'month' ? '#3b82f6' : 'white',
              color: statsFilter === 'month' ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: statsFilter === 'month' ? 'bold' : 'normal'
            }}
          >
            Месяц
          </button>
          <button
            onClick={() => {
              setStatsFilter('year');
              const today = new Date();
              const year = today.getFullYear();
              
              setStatsDateFrom(`${year}-01-01`);
              setStatsDateTo(`${year}-12-31`);
            }}
            style={{
              padding: '8px 16px',
              background: statsFilter === 'year' ? '#3b82f6' : 'white',
              color: statsFilter === 'year' ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: statsFilter === 'year' ? 'bold' : 'normal'
            }}
          >
            Год
          </button>
          <button
            onClick={() => setStatsFilter('custom')}
            style={{
              padding: '8px 16px',
              background: statsFilter === 'custom' ? '#3b82f6' : 'white',
              color: statsFilter === 'custom' ? 'white' : '#374151',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: statsFilter === 'custom' ? 'bold' : 'normal'
            }}
          >
            Свой период
          </button>
        </div>
        
        {statsFilter === 'custom' && (
          <div className="stats-date-range">
            <label style={{ fontSize: '14px' }}>С:</label>
            <input
              type="date"
              value={statsDateFrom}
              onChange={(e) => setStatsDateFrom(e.target.value)}
              style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
            <label style={{ fontSize: '14px' }}>По:</label>
            <input
              type="date"
              value={statsDateTo}
              onChange={(e) => setStatsDateTo(e.target.value)}
              style={{ padding: '8px', border: '1px solid #d1d5db', borderRadius: '4px' }}
            />
          </div>
        )}
      </div>

      {/* Заголовок с количеством дел */}
      <div style={{
        background: '#f9fafb',
        padding: '20px',
        borderRadius: '8px',
        marginBottom: '30px',
        textAlign: 'center'
      }}>
        <h2 style={{ margin: 0, fontSize: '28px', color: '#1f2937' }}>
          Выполнено дел: {totalTasks}
        </h2>
        <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
          {statsFilter === 'all' && 'За всё время'}
          {statsFilter === 'day' && 'Сегодня'}
          {statsFilter === 'month' && 'В текущем месяце'}
          {statsFilter === 'year' && 'В текущем году'}
          {statsFilter === 'custom' && `С ${statsDateFrom} по ${statsDateTo}`}
        </p>
      </div>

      <div className="stats-sections">
        <details className="compact-section stats-section" open><summary>📊 Динамика выполнения дел</summary><div className="disclosure-body">
          <p className="stats-hint">Количество завершённых дел за каждый период.</p>
          <div className="stats-periods">{allPeriods.map(period => {
            const count = tasksByPeriod[period];
            const label = dateFormat === 'hour' ? period.slice(-2) + ':00' : dateFormat === 'day' ? period.slice(8, 10) + '.' + period.slice(5, 7) : dateFormat === 'month' ? new Date(period + '-01T12:00:00').toLocaleDateString('ru-RU', { month: 'short', year: 'numeric' }) : period;
            return <div className="stats-bar-row" key={period}><div className="stats-bar-label"><span>{label}</span><strong>{count}</strong></div><div className="stats-bar-track"><span style={{ width: (count / maxCount * 100) + '%' }} /></div></div>;
          })}</div>
          {!totalTasks && <p>Нет выполненных дел за выбранный период.</p>}
        </div></details>
        <details className="compact-section stats-section" open><summary>🎨 По категориям</summary><div className="disclosure-body">
          <p className="stats-hint">Доля категории среди выполненных дел.</p>
          {categoriesArray.map(cat => <div className="stats-bar-row" key={cat.name}><div className="stats-bar-label"><span>{cat.icon} {cat.name}</span><strong>{cat.count} · {Math.round(cat.count / totalTasks * 100)}%</strong></div><div className="stats-bar-track"><span style={{ width: (cat.count / totalTasks * 100) + '%', background: cat.color }} /></div></div>)}
          {!categoriesArray.length && <p>Нет данных за выбранный период.</p>}
        </div></details>
      </div>
    </div>
  );
};
export default StatsTab;
